import MaterialIcon from '@components/common/MaterialIcon';
import { LLMUsageCheckResultStatus } from '@conversed/rest/src/contracts/llm-usages/check-results';
import type { DataSourceGenerationSummaryModalProps } from '@interfaces/TDataSourceGeneration';
import type React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const DataSourceGenerationSummaryContent: React.FC<
  DataSourceGenerationSummaryModalProps
> = ({ factCheckedData = [] }) => {
  const { t } = useTranslation();
  const { totalCorrectAnswers, totalCorrectedAnswers } = useMemo(() => {
    return factCheckedData.reduce(
      (acc, curr) => {
        const { checkResult } = curr;
        if (checkResult?.status === LLMUsageCheckResultStatus.GOOD) {
          acc.totalCorrectAnswers += 1;
        }
        if (
          checkResult?.status === LLMUsageCheckResultStatus.NEEDS_IMPROVEMENT
        ) {
          acc.totalCorrectedAnswers += 1;
        }
        return acc;
      },
      { totalCorrectAnswers: 0, totalCorrectedAnswers: 0 },
    );
  }, [factCheckedData]);

  const SUMMARY = [
    {
      text: t('train-ai.data-source-generation.summary.fact-checked-data', {
        value: factCheckedData?.length || 0,
      }),
      subText: t(
        'train-ai.data-source-generation.summary.fact-checked-data.subText',
      ),
    },
    {
      text: t(
        'train-ai.data-source-generation.summary.fact-checked-correct-answers',
        {
          value: totalCorrectAnswers,
        },
      ),
    },
    {
      text: t(
        'train-ai.data-source-generation.summary.fact-checked-corrected-answers',
        {
          value: totalCorrectedAnswers,
        },
      ),
    },
    {
      text: t('train-ai.data-source-generation.summary.hyperlinks-verified'),
      subText: t(
        'train-ai.data-source-generation.summary.hyperlinks-verified.subtext',
      ),
    },
    {
      text: t('train-ai.data-source-generation.summary.no-duplicate-questions'),
      subText: t(
        'train-ai.data-source-generation.summary.no-duplicate-questions.subtext',
      ),
    },
  ];

  return (
    <div className='h-full flex flex-col rounded-lg bg-gray-100 overflow-hidden border border-primary-dark'>
      <div className='flex bg-primary-dark text-white text-base font-normal py-1 px-3'>
        {t('train-ai.data-source-generation.summary')}
      </div>

      <div className='flex flex-col p-3 overflow-y-auto'>
        <div className='flex text-xs font-normal text-primary-base mb-7'>
          {t('train-ai.data-source-generation.summary.desc')}
        </div>

        <div className='flex flex-col gap-6 ml-4'>
          {SUMMARY.map((item, index) => (
            <div key={index} className='flex gap-4'>
              <MaterialIcon icon='check_circle' classes={['text-green-dark']} />
              <div className='flex flex-col text-sm text-primary-base'>
                <div className='font-medium'>{item.text}</div>
                <div className='font-normal'>{item.subText}</div>
              </div>
            </div>
          ))}
        </div>
        <div className='w-full bg-primary-base my-7 ring-1 ring-primary-base' />
        <div className='flex items-center'>
          <div className='flex gap-4 ml-4 text-sm text-primary-base items-center'>
            <MaterialIcon
              icon='check_circle'
              classes={['text-green-dark', 'text-6xl']}
            />
            <div className='font-medium text-lg'>
              {t('train-ai.data-source-generation.summary.ready')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataSourceGenerationSummaryContent;
